/**
 * RoadClassOverlay — OpenStreetMap arterials/collectors layer for sign-placement context.
 *
 * Renders state/US-numbered routes and arterials/collectors as colored lines.
 * Roads with a `ref=*` tag (official state/US route number) are rendered bolder
 * — that's the most reliable busy-road signal per OSM tagging guidance.
 *
 * Must be a child of react-map-gl <Map> so it can use useMap() and render
 * <Source>/<Layer> in the correct map context.
 *
 * Pattern mirrors DemographicOverlay.tsx (debounced moveend fetch).
 */

import { useCallback, useEffect } from 'react';
import { Source, Layer } from 'react-map-gl/mapbox';
import { fetchOsmRoads, type RoadFeatureProperties } from '@/services/osmApi';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';
import { useViewportGeoJSON, type ViewportFetchArgs } from '@/hooks/useViewportGeoJSON';

interface RoadClassOverlayProps {
  visible: boolean;
}

/**
 * Two zoom thresholds enable progressive disclosure:
 * - At z >= MIN_ZOOM_REGIONAL but < MIN_ZOOM_FULL: query only ref-tagged
 *   ways (state/US/county routes). Sparse, ~few hundred KB per metro.
 * - At z >= MIN_ZOOM_FULL: full composite query with all primary/secondary/
 *   tertiary roads. Heavier (~3–4 MB in dense urban areas).
 *
 * Empirical payload measurements behind these thresholds are documented in
 * docs/plans/2026-05-15-sign-table-planning-design.md. Below MIN_ZOOM_REGIONAL
 * we render nothing — the viewport is too large to query meaningfully.
 */
const MIN_ZOOM_REGIONAL = 11;
const MIN_ZOOM_FULL = 13;

const DEBOUNCE_MS = 600;

// ---------------------------------------------------------------------------
// Color + width expressions
// ---------------------------------------------------------------------------

// Color by highway class. Primary = red (busiest), tertiary = yellow (still arterial).
const HIGHWAY_COLOR = [
  'match',
  ['get', 'highway'],
  'primary', '#dc2626',
  'secondary', '#f97316',
  'tertiary', '#eab308',
  '#94a3b8', // fallback (e.g., a ref-tagged road that's not in our class set)
] as const;

// Base tier — all matched roads, thin and translucent.
const BASE_WIDTH = [
  'interpolate',
  ['linear'],
  ['zoom'],
  13, 1.5,
  16, 2.5,
  18, 3.5,
] as const;

// Bold tier — only ref-tagged roads (state/US route numbers), thicker.
const BOLD_WIDTH = [
  'interpolate',
  ['linear'],
  ['zoom'],
  13, 3,
  16, 5,
  18, 7,
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RoadClassOverlay({ visible }: RoadClassOverlayProps) {
  const setLoadingMajorRoads = useFieldOpsStore((s) => s.setLoadingMajorRoads);

  const fetchForViewport = useCallback(
    ({ bounds, zoom, signal }: ViewportFetchArgs) =>
      fetchOsmRoads(bounds, { regionalOnly: zoom < MIN_ZOOM_FULL, signal }),
    [],
  );

  const { data: geojson, loading } = useViewportGeoJSON<
    GeoJSON.FeatureCollection<GeoJSON.LineString, RoadFeatureProperties>
  >(visible, fetchForViewport, { minZoom: MIN_ZOOM_REGIONAL, debounceMs: DEBOUNCE_MS });

  // The floating legend shows a spinner while Overpass is in flight (it can take
  // 5-10s on a cold pan), and it lives outside this subtree, so the flag goes
  // through the store.
  useEffect(() => {
    setLoadingMajorRoads(loading);
  }, [loading, setLoadingMajorRoads]);

  // Make sure the spinner can't stick once the layer is switched off.
  useEffect(() => () => setLoadingMajorRoads(false), [setLoadingMajorRoads]);

  if (!geojson) return null;

  return (
    <Source id="osm-major-roads" type="geojson" data={geojson}>
      {/* Base tier — all roads matched by the composite query */}
      <Layer
        id="osm-major-roads-base"
        type="line"
        paint={{
          'line-color': HIGHWAY_COLOR as never,
          'line-width': BASE_WIDTH as never,
          'line-opacity': 0.6,
        }}
        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
      />
      {/* Bold tier — overlaid on top, only ways with a ref=* tag */}
      <Layer
        id="osm-major-roads-bold"
        type="line"
        filter={['==', ['get', 'hasRef'], true]}
        paint={{
          'line-color': HIGHWAY_COLOR as never,
          'line-width': BOLD_WIDTH as never,
          'line-opacity': 0.9,
        }}
        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
      />
    </Source>
  );
}
